import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import type * as THREE from 'three';
import type { AssetName } from './assets/catalog';
import { loadAsset } from './assets/loadAsset';
import { COMPOSITION } from './render/composition';
import { POLE_KEYS, SITE_PLACEMENTS } from './render/site';
import { WORLD } from './config/world';
import { startLoop } from './app/loop';
import { initProbe } from './app/ready';
import { TimeSystem } from './app/TimeSystem';
import type { DevParams } from './app/urlParams';
import { QualityGovernor } from './render/quality';
import { Renderer } from './render/Renderer';
import { WorldScene } from './render/Scene';
import { WeatherService } from './services/weather/WeatherService';
import { deriveWorldState } from './world/state/deriveWorldState';
import type { WorldOverrides, WorldState } from './world/state/types';

const NO_PARAMS: DevParams = { season: {}, debug: false };
/** Recompute WorldState at ~10 Hz; the render interpolates nothing yet in phase 1 (state changes slowly). */
const STATE_INTERVAL_MS = 100;

async function main(): Promise<void> {
  const probe = initProbe();
  // __DEV_TOOLS__ is a build-time literal: in prod this whole branch and the parser are removed.
  const params = __DEV_TOOLS__ ? (await import('./app/urlParams')).parseDevParams(location.search) : NO_PARAMS;
  if (__DEV_TOOLS__) console.info('tsukimachi:dev-tools enabled', params);
  const config = params.seed === undefined ? WORLD : { ...WORLD, seed: params.seed };

  const time = new TimeSystem(
    {
      timeZone: config.timeZone,
      timeMode: config.timeMode,
      worldEpoch: config.worldEpoch,
      archiveFloor: config.archiveFloor,
      viewerTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      allowBeforeEpoch: __DEV_TOOLS__,
      start: params.start,
      speed: params.speed,
    },
    Date.now(),
  );
  const weather = new WeatherService({ seed: config.seed, timeZone: config.timeZone, override: params.weather });
  const overrides: WorldOverrides = { season: params.season };

  const renderer = new Renderer(document.body, params.quality ?? 'med');
  const world = new WorldScene(config, renderer.renderer);

  const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
  const base = import.meta.env.BASE_URL;
  // One load per asset kind (in parallel); repeated placements (poles) get clones.
  const placements = { ...COMPOSITION, ...SITE_PLACEMENTS };
  const kinds = [...new Set(Object.values(placements).map((p) => p.asset))];
  const loaded = new Map(await Promise.all(kinds.map(async (k) => [k, await loadAsset(k, loader, base)] as const)));
  const used = new Set<AssetName>();
  const placed = new Map<string, THREE.Object3D>();
  for (const [key, placement] of Object.entries(placements)) {
    const source = loaded.get(placement.asset)!;
    const object = used.has(placement.asset) ? source.clone(true) : source;
    used.add(placement.asset);
    world.place(object, placement);
    placed.set(key, object);
  }
  world.addFoliage(placed.get('sakura_v0')!);
  world.addWires(POLE_KEYS.map((k) => placed.get(k)!));
  world.addFence(await loadAsset('fence_segment', loader, base));
  if (__DEV_TOOLS__ && params.clay) world.applyClay();
  world.lighting.sun.shadow.mapSize.setScalar(renderer.shadowMapSize);
  world.finalize(renderer.camera);
  renderer.attach(world.scene);
  if (!world.sky.floatTargets) renderer.setQuality('low');
  const governor = new QualityGovernor(renderer.level, Date.now(), params.quality !== undefined || !world.sky.floatTargets);

  let state: WorldState | undefined;
  let forceLut = false;
  let lastStateAt = -Infinity;
  const computeState = (realNow: number): WorldState => {
    const { instant } = time.sample(realNow);
    return deriveWorldState(instant, weather.historyFor(instant), config, overrides);
  };

  if (__DEV_TOOLS__ && (params.fly || params.view)) {
    const { attachFlyCamera, setReviewView } = await import('./debug/flyCamera');
    if (params.view) setReviewView(renderer.camera, params.view);
    if (params.fly) attachFlyCamera(renderer.camera, renderer.renderer.domElement);
  }

  if (__DEV_TOOLS__ && params.debug) {
    const { createDebugPanel } = await import('./debug/DebugPanel');
    createDebugPanel({
      time,
      weather,
      overrides,
      renderer,
      getState: () => state,
      invalidate: () => (lastStateAt = -Infinity),
      onGradeChange: () => (forceLut = true),
    });
  }

  let framesAfterLoad = 0;
  startLoop((realNow, dt) => {
    const next = governor.frame(realNow, dt * 1000);
    if (next) {
      renderer.setQuality(next);
      world.lighting.sun.shadow.mapSize.setScalar(renderer.shadowMapSize);
      world.lighting.sun.shadow.map?.dispose();
      world.lighting.sun.shadow.map = null;
    }
    if (realNow - lastStateAt >= STATE_INTERVAL_MS) {
      state = computeState(realNow);
      lastStateAt = realNow;
      const levels = world.update(state, time.sample(realNow).animTime, renderer.camera, realNow);
      renderer.updatePost(levels.exposure, state.sky.sun.altDeg, forceLut);
      forceLut = false;
      if (__DEV_TOOLS__) probe.state = state;
    }
    renderer.render(world.scene, dt);
    // Two rendered frames after everything is loaded: shaders compiled, state applied.
    if (!probe.ready && ++framesAfterLoad >= 2) probe.ready = true;
  });
}

void main();
